import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { ReactComponent as PrevIcon } from '../../../assets/arrow-prev.svg';
import { ReactComponent as NextIcon } from '../../../assets/arrow-next.svg';
import { ReactComponent as DownArrow } from '../../../assets/down-arrow.svg';
import style from './Calendar.module.css';
import zeplinColor from '../../../theme/Color';
import CalendarPanel from './CalendarPanel/CalendarPanel';
import CalendarMain from './CalendarMain/CalendarMain';
import dayjs, { Dayjs } from 'dayjs';
import CustomPopOver from '../../shared/CustomPopOver/CustomPopOver';

const Calendar = () => {
  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: dayjs(),
    endDate: dayjs().add(6, 'day'),
  });
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [currentWeekDate, setCurrentWeekDate] = useState(dayjs());
  const [currentMonth, setCurrentMonth] = useState(dayjs().month()); // 0-11
  const [currentYear, setCurrentYear] = useState(dayjs().year());
  const [selectionMode, setSelectionMode] = useState<'month' | 'year'>('month');
  const [anchorEl, setAnchorEl] = React.useState<HTMLDivElement | null>(null);
  const [collapsed, setCollapsed] = useState(false);

  const open = Boolean(anchorEl);
  const id = open ? 'simple-popover' : undefined;

  const handleDateChange = (date: Dayjs | null) => {
    setSelectedDate(date);
    if (date) {
      // const newWeekDate = date.startOf('isoWeek');
      const newMonth = date.month();
      const newYear = date.year();
      setCurrentWeekDate(date);

      // Update month if changed
      if (newMonth !== currentMonth) {
        setCurrentMonth(newMonth);
      }

      // Update year if changed
      if (newYear !== currentYear) {
        setCurrentYear(newYear);
      }
    }
  };
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ];
  const shortMonths = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];

  //  Generate years array (6 before, current, 5 ahead)
  const generateYears = () => {
    const years = [];
    for (let i = currentYear - 6; i <= currentYear + 5; i++) {
      years.push(i);
    }
    return years;
  };

  const years = generateYears();

  //  Handle month selection
  const handleMonthSelect = (monthIndex: number) => {
    setCurrentMonth(monthIndex);

    const newWeekDate = dayjs().year(currentYear).month(monthIndex).startOf('month'); // Go to first day of selected month

    console.log('Updated currentWeekDate for month selection:', {
      oldWeekDate: currentWeekDate.format('YYYY-MM-DD'),
      newWeekDate: newWeekDate.format('YYYY-MM-DD'),
    });

    // Update current week date
    setCurrentWeekDate(newWeekDate);

    //  Also update selected date to the new week
    setSelectedDate(newWeekDate.startOf('isoWeek'));
    setSelectedDateRange({
      startDate: newWeekDate.startOf('isoWeek'),
      endDate: newWeekDate.endOf('isoWeek'),
    });
    setAnchorEl(null); // Close popover
  };

  //  Handle year selection
  const handleYearSelect = (year: number) => {
    setCurrentYear(year);

    const newWeekDate = dayjs().year(year).month(currentMonth).startOf('month'); // Go to first day of current month in selected year

    console.log('Updated currentWeekDate for year selection:', {
      oldWeekDate: currentWeekDate.format('YYYY-MM-DD'),
      newWeekDate: newWeekDate.format('YYYY-MM-DD'),
    });

    // Update current week date
    setCurrentWeekDate(newWeekDate);

    //  Also update selected date to the new week
    setSelectedDate(newWeekDate.startOf('isoWeek'));
    setSelectedDateRange({
      startDate: newWeekDate.startOf('isoWeek'),
      endDate: newWeekDate.endOf('isoWeek'),
    });
    setAnchorEl(null); // Close popover
  };

  const handlePrevious = () => {
    console.log('Previous week clicked');
    const previousWeek = currentWeekDate.subtract(1, 'week');
    const previousWeekMonth = previousWeek.month();
    const previousWeekYear = previousWeek.year();
    setCurrentWeekDate(previousWeek);

    // Update selected date range to show the new week
    setSelectedDate(previousWeek.startOf('isoWeek'));

    if (previousWeekMonth !== currentMonth) {
      console.log('Month changed from', currentMonth, 'to', previousWeekMonth);
      setCurrentMonth(previousWeekMonth);
    }

    if (previousWeekYear !== currentYear) {
      console.log('Year changed from', currentYear, 'to', previousWeekYear);
      setCurrentYear(previousWeekYear);
    }
  };

  const handleNext = () => {
    console.log('Next week clicked');
    const nextWeek = currentWeekDate.add(1, 'week');
    const nextWeekMonth = nextWeek.month();
    const nextWeekYear = nextWeek.year();
    setCurrentWeekDate(nextWeek);

    // Update selected date range to show the new week
    setSelectedDate(nextWeek.startOf('isoWeek'));
    // setSelectedDateRange({
    //   startDate: nextWeek.startOf('isoWeek'),
    //   endDate: nextWeek.endOf('isoWeek'),
    // });
    if (nextWeekMonth !== currentMonth) {
      console.log('Month changed from', currentMonth, 'to', nextWeekMonth);
      setCurrentMonth(nextWeekMonth);
    }

    if (nextWeekYear !== currentYear) {
      console.log('Year changed from', currentYear, 'to', nextWeekYear);
      setCurrentYear(nextWeekYear);
    }
  };
  const handleMonthYearChange = (month: number, year: number) => {
    console.log('Calendar: Month/Year changed from CalendarPanel', { month, year });
    setCurrentMonth(month);
    setCurrentYear(year);
  };

  return (
    <Box>
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          right: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '24px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {/*  Display current month dynamically */}
          <Typography
            variant='responsiveHeading3'
            onClick={(e: any) => {
              setAnchorEl(e.currentTarget);
              setSelectionMode('month');
            }}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                color: zeplinColor.Brand1,
              },
            }}
          >
            {months[currentMonth]}{' '}
          </Typography>

          {/*  Display current year dynamically */}
          <Typography
            variant='responsiveHeading3'
            onClick={(e: any) => {
              setAnchorEl(e.currentTarget);
              setSelectionMode('year');
            }}
            sx={{
              cursor: 'pointer',
              '&:hover': {
                color: zeplinColor.Brand1,
              },
            }}
          >
            {currentYear}
          </Typography>

          {/*  Add click handlers to navigation */}
          <Box sx={{ display: 'flex', gap: '4px' }}>
            <Box
              sx={{
                padding: '5px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',

                borderRadius: '50%',
                '&:hover': { backgroundColor: zeplinColor.Neutral4 },
              }}
            >
              <PrevIcon
                className={style['next-prev-icon']}
                onClick={handlePrevious}
                style={{ cursor: 'pointer' }}
              />
            </Box>
            <Box
              sx={{
                padding: '5px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',

                borderRadius: '50%',
                '&:hover': { backgroundColor: zeplinColor.Neutral4 },
              }}
            >
              <NextIcon
                className={style['next-prev-icon']}
                onClick={handleNext}
                style={{ cursor: 'pointer' }}
              />
            </Box>
          </Box>
        </Box>

        {/* <Box
          sx={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            borderRadius: '50px',
            backgroundColor: zeplinColor.Neutral3,
            padding: '15px 24px',
            '&:hover': {
              backgroundColor: zeplinColor.Neutral4,
            },
          }}
        >
          <Typography variant='responsiveBody3'>Week</Typography>
        </Box> */}
      </Box>

      <Box sx={{ display: 'flex', gap: { xs: '12px', xl: '24px' } }}>
        <Box
          sx={{
            // ...(!collapsed && { flex: 1 }),
            flex: collapsed ? 0 : 1,
            // transform: collapsed ? 'translateX(-100%)' : 'translateX(0)',
            transition: 'flex 0.3s ease-in-out',
          }}
        >
          {/*  Pass current month/year to CalendarPanel */}
          <CalendarPanel
            onDateChange={handleDateChange}
            currentMonth={currentMonth}
            currentYear={currentYear}
            onMonthYearChange={handleMonthYearChange}
            currentWeekDate={currentWeekDate}
            collapsed={collapsed}
            setCollapsed={setCollapsed}
          />
        </Box>
        <Box
          sx={{
            flex: 3,
            overflowX: 'auto',
          }}
        >
          <CalendarMain selectedDateRange={selectedDateRange} selectedDate={selectedDate} />
        </Box>
      </Box>

      <CustomPopOver
        setAnchorEl={setAnchorEl}
        anchorEl={anchorEl}
        id={id}
        sx={{
          borderRadius: '14px',
          boxShadow: 'rgba(0, 0, 0, 0.15)',
          marginTop: '8px',
          marginRight: '2rem',
          marginLeft: '-4rem',
        }}
      >
        <Box sx={{ padding: '16px', minWidth: '280px' }}>
          {/*  Enhanced Month Selection */}
          {selectionMode === 'month' && (
            <>
              {/* <Typography
                variant='h6'
                sx={{
                  marginBottom: '16px',
                  textAlign: 'center',
                  color: zeplinColor.BasicBlack,
                  fontWeight: 600,
                  fontSize: '16px',
                }}
              >
                Select Month - {currentYear}
              </Typography> */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '12px',
                }}
              >
                {shortMonths.map((month, index) => (
                  <Box
                    key={month}
                    onClick={() => handleMonthSelect(index)}
                    sx={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor:
                        index === currentMonth ? zeplinColor.BasicBlack : zeplinColor.Neutral1,
                      color: index === currentMonth ? zeplinColor.Neutral1 : zeplinColor.BasicBlack,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor:
                          index === currentMonth ? zeplinColor.BasicBlack : zeplinColor.Neutral3,
                        // transform: 'scale(1.02)',
                      },
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: index === currentMonth ? 600 : 400,
                        fontSize: '14px',
                      }}
                    >
                      {month}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}

          {/*  Enhanced Year Selection */}
          {selectionMode === 'year' && (
            <>
              {/* <Typography
                variant='h6'
                sx={{
                  marginBottom: '16px',
                  textAlign: 'center',
                  color: zeplinColor.BasicBlack,
                  fontWeight: 600,
                  fontSize: '16px',
                }}
              >
                Select Year
              </Typography> */}
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(4, 1fr)',
                  gap: '12px',
                  maxHeight: '240px',
                  overflowY: 'auto',
                }}
              >
                {years.map((year: number, index: number) => (
                  <Box
                    key={year}
                    onClick={() => handleYearSelect(year)}
                    sx={{
                      padding: '12px 8px',
                      textAlign: 'center',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      backgroundColor:
                        year === currentYear ? zeplinColor.BasicBlack : zeplinColor.Neutral1,
                      color: year === currentYear ? zeplinColor.Neutral1 : zeplinColor.BasicBlack,
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        backgroundColor:
                          year === currentYear ? zeplinColor.BasicBlack : zeplinColor.Neutral3,
                        transform: 'scale(1.02)',
                      },
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: year === currentYear ? 600 : 400,
                        fontSize: '14px',
                      }}
                    >
                      {year}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          )}
        </Box>
      </CustomPopOver>
    </Box>
  );
};

export default Calendar;
